// ...existing imports...
import { useTranslation } from 'react-i18next';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button/Button';
// Replace previous LoadingButton import (was destructured) with direct import if not already.
// SVG assets (ensure these paths exist; adjust if icons live elsewhere)
import Card from '@mui/material/Card';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import OpenInNewIcon from '~/assets/images/arrow-up-right.svg';
import LearnIcon from '~/assets/images/e-learn.svg';
import { useBrandInfo } from '~/hooks/useBrandInfo';
import useResponsive from '~/hooks/useResponsive';
import { useSiteContext } from '~/scenes/Site';
import { isAiSite } from '~/utils/site/helpers';

import VideoArticleCard from './VideoArticleCard';

interface HowtoCardProps {
  title?: string;
  aiImageName?: string;
  description?: string;
  articleUrl?: string;
  displayVideoArticleCard?: boolean;
  showHeader?: boolean; // Optional prop to control header display
  embedded?: boolean;
}
const HowtoCard = ({
  title = '',
  aiImageName = '',
  description = '',
  articleUrl = '',
  displayVideoArticleCard = true,
  showHeader = true, // Default to true if not provided
  embedded = false,
}: HowtoCardProps) => {
  const { t } = useTranslation('sites', { keyPrefix: 'howtoCard' });
  const {
    localizedBrandName,
    brandFromJWT,
    howToArticleUrl,
    howToWebsitesAIArticleUrl,
    howToWebsitesArticleUrl,
  } = useBrandInfo();
  const brand = brandFromJWT?.toLowerCase() || '';
  const { data: siteInfo } = useSiteContext();
  const isAiBuilderSite = isAiSite(siteInfo);
  const showVideoArticleCard =
    displayVideoArticleCard && !isAiBuilderSite && brand !== 'hostgator';

  const defaultArticleUrl = isAiBuilderSite
    ? howToWebsitesAIArticleUrl
    : howToWebsitesArticleUrl;
  const learnArticle = articleUrl || defaultArticleUrl;

  const { isMobile } = useResponsive();
  const isMobileView = isMobile();

  const handleLearnMore = (e: React.MouseEvent) => {
    e.preventDefault();
    window.open(howToArticleUrl, '_blank', 'noopener,noreferrer');
  };

  const cardContent = learnArticle ? (
    <Card
      data-testid="howto-card"
      sx={{
        display: 'flex',
        flexDirection: isMobileView ? 'column' : 'row',
        alignItems: 'center',
        p: 2,
        gap: isMobileView ? 1 : 2,
      }}
    >
      {/* Icon */}
      <Stack
        alignItems="center"
        justifyContent="center"
        sx={{
          mb: isMobileView ? 1 : 0,
          flex: isMobileView ? '0 0 auto' : '0 0 40px',
        }}
      >
        <Box
          component="img"
          src={LearnIcon}
          aria-hidden="true"
          data-testid="howto-icon"
          sx={{ width: 32, height: 32 }}
        />
      </Stack>
      {/* Text Content */}
      <Stack spacing={1} sx={{ textAlign: 'left', flex: 1 }}>
        <Typography variant="h3" data-testid="howto-title">
          {title
            ? t(title, {
                applicationName: aiImageName,
              })
            : t('siteDetail.title')}
        </Typography>
        <Typography
          variant="body2"
          color="text.secondary"
          data-testid="howto-description"
          maxWidth="720px"
        >
          {description
            ? t(description, {
                brand: localizedBrandName,
                applicationName: aiImageName,
              })
            : t('description')}
        </Typography>
      </Stack>
      {/* Action Button */}
      <Button
        variant="outlined"
        color="inherit"
        fullWidth={isMobileView}
        href={learnArticle}
        target="_blank"
        rel="noopener noreferrer"
        startIcon={
          <Box
            component="img"
            src={OpenInNewIcon}
            aria-hidden="true"
            width="18px"
            height="18px"
          />
        }
        data-testid="howto-read-article-btn"
        sx={{
          mt: isMobileView ? 1 : 0,
          alignSelf: isMobileView ? 'stretch' : 'center',
          justifyContent: 'center',
          textTransform: 'none',
        }}
      >
        {t('buttonLearnMore')}
      </Button>
    </Card>
  ) : null;

  if (embedded) {
    return displayVideoArticleCard ? (
      <VideoArticleCard
        titleI18nKey="videoArticleCard.title"
        descriptionI18nKey="videoArticleCard.description"
        embedded
      />
    ) : (
      cardContent
    );
  }

  return (
    <Stack spacing={2} sx={{ mt: 4 }}>
      {showVideoArticleCard ? (
        <VideoArticleCard
          titleI18nKey="videoArticleCard.title"
          descriptionI18nKey="videoArticleCard.description"
        />
      ) : (
        learnArticle && (
          <>
            {showHeader && (
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
              >
                <Typography variant="h2" data-testid="howto-header">
                  {t('header')}
                </Typography>
                <Link
                  onClick={handleLearnMore}
                  underline="none"
                  sx={{ fontSize: 14, textDecoration: 'none' }}
                  data-testid="howto-learn-all-link"
                >
                  {t('learnAllLink')}
                </Link>
              </Stack>
            )}

            {cardContent}
          </>
        )
      )}
    </Stack>
  );
};

export default HowtoCard;
